import { useEffect, useRef } from 'react';
import { MergeView } from '@codemirror/merge';
import { EditorState } from '@codemirror/state';
import { EditorView, lineNumbers } from '@codemirror/view';
import { json as jsonLang } from '@codemirror/lang-json';
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';

/**
 * Read-only side-by-side diff of two JSON documents using CodeMirror's MergeView. Changed regions are
 * highlighted and connected across the two panes; unchanged runs are collapsed to keep the diff compact.
 * Used by the Configuration Store save confirmation so an editor can review Before (stored) vs After
 * (edited) before persisting.
 */
export function JsonDiffView({ before, after, height = '55vh' }: { before: string; after: string; height?: string }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<MergeView | null>(null);

  useEffect(() => {
    if (!hostRef.current) {
      return;
    }
    const readOnly = [
      lineNumbers(),
      jsonLang(),
      syntaxHighlighting(defaultHighlightStyle),
      EditorView.editable.of(false),
      EditorState.readOnly.of(true),
      EditorView.lineWrapping,
    ];
    const view = new MergeView({
      a: { doc: before, extensions: readOnly },
      b: { doc: after, extensions: readOnly },
      parent: hostRef.current,
      // Collapse long stretches of identical lines so the reviewer sees only what changed (plus context).
      collapseUnchanged: { margin: 3, minSize: 4 },
      highlightChanges: true,
      gutter: true,
    });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [before, after]);

  return <div ref={hostRef} style={{ height, overflow: 'auto' }} />;
}
